import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { Loader2, MoveRight, Search } from 'lucide-react';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import { db } from '../../utils/firebase';
import type { EnhancedDeal, Lead, TimberProject } from '../../types';
import { NameProjectFields, reserveProjectNumberOnSubmit } from '../projects/NameProjectFields';
import type { NameProjectFieldsValue } from '../../utils/projectName';
import { defaultClientLabelFromLead, isProjectNumber } from '../../utils/projectName';
import { moveStructureGroupToProject } from '../../utils/moveStructureGroupService';
import type { ProjectFile, ProjectFileStream } from '../../types';
import './ProjectFiles.css';
import './SplitTracksModal.css';
import './MoveStructureGroupModal.css';

export interface MoveStructureGroupModalProps {
  groupName: string;
  groupKey: string;
  sourceProjectId: string;
  sourceDealId: string;
  sourceLeadId?: string;
  streams: ProjectFileStream[];
  files: ProjectFile[];
  onClose: () => void;
}

function projectLine(project: TimberProject, deal: EnhancedDeal | undefined): string {
  const num = (deal?.projectNumber || '').trim();
  const name = project.name || deal?.descriptor || deal?.title || 'Project';
  return num ? `${num} ${name}` : name;
}

const MoveStructureGroupModal: React.FC<MoveStructureGroupModalProps> = ({
  groupName,
  groupKey,
  sourceProjectId,
  sourceDealId,
  sourceLeadId,
  streams,
  files,
  onClose,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    leads,
    enhancedDeals,
    timberProjects,
    pipelines,
    createProjectForLead,
    createPreconProject,
  } = useCRM();

  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [search, setSearch] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [leadId, setLeadId] = useState(sourceLeadId || '');
  const [assignNumber, setAssignNumber] = useState(false);
  const [naming, setNaming] = useState<NameProjectFieldsValue>(() => ({
    projectNumber: '',
    clientLabel: defaultClientLabelFromLead(leads.find(lead => lead.id === sourceLeadId)),
    descriptor: groupName.slice(0, 40),
  }));
  const [namingError, setNamingError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!leadId || naming.clientLabel.trim()) return;
    const lead = leads.find(item => item.id === leadId);
    const label = defaultClientLabelFromLead(lead);
    if (!label) return;
    setNaming(prev => (prev.clientLabel.trim() ? prev : { ...prev, clientLabel: label }));
  }, [leadId, leads, naming.clientLabel]);

  const activeLeads = useMemo(
    () =>
      leads
        .filter(lead => !lead.archived)
        .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)),
    [leads],
  );

  const dealsById = useMemo(() => {
    const map = new Map<string, EnhancedDeal>();
    enhancedDeals.forEach(deal => {
      if (deal.id) map.set(deal.id, deal);
    });
    return map;
  }, [enhancedDeals]);

  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return timberProjects
      .filter(project => {
        if (!project.id || project.id === sourceProjectId) return false;
        if (!project.dealId || project.dealId === sourceDealId) return false;
        if (!q) return true;
        const deal = dealsById.get(project.dealId);
        const hay = [
          project.name,
          project.clientName,
          deal?.title,
          deal?.projectNumber,
          deal?.descriptor,
          deal?.clientLabel,
          deal?.contactName,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => {
        const aSame = dealsById.get(a.dealId)?.leadId === sourceLeadId ? 0 : 1;
        const bSame = dealsById.get(b.dealId)?.leadId === sourceLeadId ? 0 : 1;
        if (aSame !== bSame) return aSame - bSame;
        const client = (a.clientName || '').localeCompare(b.clientName || '');
        if (client !== 0) return client;
        return (a.name || '').localeCompare(b.name || '');
      })
      .slice(0, 40);
  }, [timberProjects, search, sourceProjectId, sourceDealId, sourceLeadId, dealsById]);

  const thisClient = candidates.filter(project => dealsById.get(project.dealId)?.leadId === sourceLeadId);
  const otherClients = candidates.filter(project => dealsById.get(project.dealId)?.leadId !== sourceLeadId);

  const canMove =
    !busy &&
    (mode === 'existing'
      ? !!selectedProjectId
      : !!leadId && !!naming.clientLabel.trim() && !!naming.descriptor.trim() && (!assignNumber || isProjectNumber(naming.projectNumber)));

  const selectLead = (id: string) => {
    setLeadId(id);
    const lead = leads.find(item => item.id === id);
    setNaming(prev => ({
      ...prev,
      clientLabel: defaultClientLabelFromLead(lead) || prev.clientLabel,
    }));
  };

  const move = async () => {
    if (!canMove || !user?.id) return;
    setBusy(true);
    setError(null);
    try {
      let destinationProjectId = selectedProjectId;
      let destinationDealId = '';
      let proposalSeed: {
        clientName?: string;
        projectName?: string;
        location?: string;
        clientLabel?: string;
        descriptor?: string;
        projectNumber?: string;
      } | undefined;

      if (mode === 'existing') {
        const project = timberProjects.find(item => item.id === selectedProjectId);
        if (!project?.dealId) throw new Error('That project is not linked to a deal.');
        destinationDealId = project.dealId;
        const deal = dealsById.get(project.dealId);
        proposalSeed = {
          clientName: project.clientName || deal?.contactName,
          projectName: deal?.descriptor || project.name,
          location: project.location || deal?.location,
          clientLabel: deal?.clientLabel,
          descriptor: deal?.descriptor,
          projectNumber: deal?.projectNumber,
        };
      } else {
        const lead = leads.find(item => item.id === leadId);
        if (!lead) throw new Error('Choose a client.');
        const pipeline = pipelines.find(item => item.isDefault) || pipelines[0];
        if (!pipeline?.id) throw new Error('No pipeline found.');
        const reserved = assignNumber ? await reserveProjectNumberOnSubmit() : '';
        destinationDealId = await createProjectForLead(
          lead,
          naming.descriptor.trim(),
          lead.projectType || 'Custom',
          pipeline.id,
          user.id,
          undefined,
          {
            ...(reserved ? { projectNumber: reserved } : {}),
            clientLabel: naming.clientLabel.trim(),
            descriptor: naming.descriptor.trim(),
          },
        );
        const dealSnap = await getDoc(doc(db, 'enhancedDeals', destinationDealId));
        if (!dealSnap.exists()) throw new Error('The new project could not be opened.');
        const deal = { id: destinationDealId, ...dealSnap.data() } as EnhancedDeal;
        const existingHub = await getDocs(
          query(collection(db, 'timberProjects'), where('dealId', '==', destinationDealId)),
        );
        destinationProjectId = existingHub.empty
          ? await createPreconProject(deal, user.id)
          : existingHub.docs[0].id;
        proposalSeed = {
          clientName: `${lead.firstName} ${lead.lastName}`.trim(),
          projectName: naming.descriptor.trim(),
          location: lead.location,
          clientLabel: naming.clientLabel.trim(),
          descriptor: naming.descriptor.trim(),
          projectNumber: reserved || undefined,
        };
      }

      await moveStructureGroupToProject({
        sourceProjectId,
        sourceDealId,
        destinationProjectId,
        destinationDealId,
        groupKey,
        streams,
        files,
        createdBy: user.id,
        proposalSeed,
      });
      navigate(`/project-hub/${destinationProjectId}?tab=files`);
      onClose();
    } catch (err) {
      console.error('[MoveStructureGroup]', err);
      setError(err instanceof Error ? err.message : 'Could not move this group.');
      setBusy(false);
    }
  };

  const renderProject = (project: TimberProject) => {
    const deal = dealsById.get(project.dealId);
    const selected = project.id === selectedProjectId;
    return (
      <button
        key={project.id}
        type="button"
        className={`pf-move-group-item${selected ? ' is-selected' : ''}`}
        aria-pressed={selected}
        disabled={busy}
        onClick={() => setSelectedProjectId(project.id!)}
      >
        <span className="pf-move-group-item-name">{projectLine(project, deal)}</span>
        <span className="pf-move-group-item-client">{project.clientName || deal?.contactName || 'Client'}</span>
      </button>
    );
  };

  return (
    <div
      className="pf-modal-overlay"
      role="presentation"
      onMouseDown={event => event.target === event.currentTarget && !busy && onClose()}
    >
      <div className="pf-modal pf-modal--move pf-modal--move-group" role="dialog" aria-modal="true" aria-labelledby="pf-move-group-title">
        <h3 className="pf-modal-title" id="pf-move-group-title">
          <MoveRight size={16} aria-hidden /> Move {groupName}
        </h3>
        <p className="pf-modal-hint">
          {groupName}’s structures and the file versions on its tracks move to the project you choose.
          Comments and markup on those files move with them. Whole-project files stay on this project.
          Tasks, quotes, and portal releases stay here.
        </p>

        <div className="pf-move-choice" role="radiogroup" aria-label="Destination">
          <label>
            <input
              type="radio"
              name="pf-move-group-mode"
              checked={mode === 'existing'}
              disabled={busy}
              onChange={() => setMode('existing')}
            />
            Existing project
          </label>
          <label>
            <input
              type="radio"
              name="pf-move-group-mode"
              checked={mode === 'new'}
              disabled={busy}
              onChange={() => setMode('new')}
            />
            New project
          </label>
        </div>

        {mode === 'existing' ? (
          <div className="pf-move-group-pane">
            <label className="pf-split-field">
              <span className="pf-vr-mu">Search</span>
              <span className="pf-move-group-search">
                <Search size={14} aria-hidden />
                <input
                  type="text"
                  className="pf-text-input pf-text-input-height"
                  value={search}
                  disabled={busy}
                  placeholder="Client, project, or number"
                  onChange={event => setSearch(event.target.value)}
                />
              </span>
            </label>
            <div className="pf-move-group-list">
              {thisClient.length > 0 ? (
                <>
                  <p className="pf-move-group-section">This client</p>
                  {thisClient.map(renderProject)}
                </>
              ) : null}
              {otherClients.length > 0 ? (
                <>
                  <p className="pf-move-group-section">{thisClient.length > 0 ? 'Other clients' : 'Projects'}</p>
                  {otherClients.map(renderProject)}
                </>
              ) : null}
              {candidates.length === 0 ? <p className="pf-move-group-empty">No other projects match.</p> : null}
            </div>
          </div>
        ) : (
          <div className="pf-move-group-pane">
            <label className="pf-split-field">
              <span className="pf-vr-mu">Client</span>
              <select
                className="pf-tag-select pf-text-input-height"
                value={leadId}
                disabled={busy}
                onChange={event => selectLead(event.target.value)}
              >
                <option value="">Choose a client…</option>
                {activeLeads.map(lead => (
                  <option key={lead.id} value={lead.id}>
                    {leadLabel(lead)}
                  </option>
                ))}
              </select>
            </label>
            {leadId ? (
              <>
                <label className="pf-move-group-check">
                  <input
                    type="checkbox"
                    checked={assignNumber}
                    disabled={busy}
                    onChange={event => setAssignNumber(event.target.checked)}
                  />
                  <span>Assign a project number</span>
                </label>
                <NameProjectFields
                  mode="create"
                  value={naming}
                  onChange={setNaming}
                  onError={setNamingError}
                  disabled={busy}
                  deferProjectNumber={!assignNumber}
                  deferredNumberNote="No project number unless you assign one."
                />
                {namingError ? <p className="pf-move-group-error">{namingError}</p> : null}
              </>
            ) : null}
          </div>
        )}

        {error ? <p className="pf-move-group-error">{error}</p> : null}

        <div className="pf-modal-actions">
          <button type="button" className="pf-modal-cancel" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="pf-upload-btn pf-upload-btn--accent" disabled={!canMove} onClick={() => void move()}>
            {busy ? (
              <>
                <Loader2 size={14} className="pf-spin" /> Moving…
              </>
            ) : (
              <>
                <MoveRight size={14} aria-hidden /> Move group
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

function leadLabel(lead: Lead): string {
  const name = `${lead.firstName || ''} ${lead.lastName || ''}`.trim();
  return lead.company ? `${name} · ${lead.company}` : name || 'Client';
}

export default MoveStructureGroupModal;
